import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {User} from '../../types/models';
import {UserType} from '../../types/enums';

const STORAGE_KEY = 'kiwi-price-net';

/**
 * Corporate customers see net prices (with the label "netto"), all others gross prices without label.
 * The flag is stored, so the first view after the start already shows the right prices.
 */
@Injectable({
  providedIn: 'root'
})
export class PriceDisplayService {

  private netSubject = new BehaviorSubject<boolean>(PriceDisplayService.load());

  private static load(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  get net(): boolean {
    return this.netSubject.value;
  }

  get Net(): Observable<boolean> {
    return this.netSubject.asObservable();
  }

  // called with every loaded user (null: guest or logged out)
  setUser(user: User) {
    const net = user?.Type === UserType.CORPORATE;
    try {
      localStorage.setItem(STORAGE_KEY, net ? '1' : '0');
    } catch (e) {
    }
    if (net !== this.netSubject.value) {
      this.netSubject.next(net);
    }
  }

  // net price in cent of a gross price with the tax rate (e.g. 7, 19)
  toNet(gross: number, tax: number): number {
    return Math.round((gross || 0) / (1 + (Number(tax) || 0) / 100));
  }

  // displayed price in cent: net for corporate customers (only with known tax rate), otherwise gross
  getDisplayed(gross: number, tax: number): { Value: number, Net: boolean } {
    if (gross == null) {
      return { Value: gross, Net: false };
    }
    if (!this.net || tax == null) {
      return { Value: gross, Net: false };
    }
    return { Value: this.toNet(gross, tax), Net: true };
  }

  /**
   * Sums of positions [{ Gross (cent, total of the position), Tax }]: net sum and tax per rate, gross sum.
   */
  getTaxSummary(positions: { Gross: number, Tax: number }[]): { Net: number, Gross: number, Taxes: { Tax: number, Value: number }[] } {
    const byTax = new Map<number, number>();
    for (const position of positions || []) {
      const tax = Number(position.Tax) || 0;
      byTax.set(tax, (byTax.get(tax) || 0) + (position.Gross || 0));
    }
    let net = 0;
    let gross = 0;
    const taxes = [];
    for (const [tax, sum] of Array.from(byTax.entries()).sort((a, b) => a[0] - b[0])) {
      const sumNet = this.toNet(sum, tax);
      net += sumNet;
      gross += sum;
      if (tax > 0) {
        taxes.push({ Tax: tax, Value: sum - sumNet });
      }
    }
    return { Net: net, Gross: gross, Taxes: taxes };
  }
}
